import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, Building2, GraduationCap, Handshake } from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, homeFor } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const ROLES = [
  { id: "candidate", label: "Candidate", desc: "Find jobs, build your CV, learn", icon: Briefcase },
  { id: "employer_owner", label: "Employer", desc: "Hire and manage your workforce", icon: Building2 },
  { id: "franchise_owner", label: "Hiring Partner", desc: "Source candidates, earn payouts", icon: Handshake },
  { id: "institute_owner", label: "Training Institute", desc: "Sell courses, issue certificates", icon: GraduationCap },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") || "candidate");
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const needsOrg = role !== "candidate";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await register({ ...form, role, company_name: form.company_name || undefined });
      toast.success("Account created. Welcome to Job Spring!");
      navigate(homeFor(user));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="navy-bg noise-overlay hidden lg:flex flex-col justify-between p-12 relative">
        <Link to="/"><Brand dark /></Link>
        <div className="relative z-10">
          <h1 className="text-4xl font-heading font-extrabold text-white leading-tight">
            One Platform.<br />Complete Workforce Management.
          </h1>
          <p className="text-white/60 mt-4 max-w-md">
            Join as a candidate, employer, hiring partner or training institute.
          </p>
        </div>
        <p className="text-white/40 text-sm relative z-10">Empowering Careers, Every Day.</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-lg" data-testid="register-page">
          <Link to="/" className="lg:hidden inline-block mb-8"><Brand /></Link>
          <h2 className="text-3xl font-heading font-bold text-[#0B1B3D]">Create your account</h2>
          <p className="text-sm text-muted-foreground mt-2">Choose how you want to use Job Spring.</p>

          <div className="grid grid-cols-2 gap-3 mt-8" role="radiogroup" aria-label="Account type">
            {ROLES.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRole(r.id)}
                data-testid={`role-${r.id}`}
                className={`text-left border rounded-xl p-4 transition-colors ${
                  role === r.id
                    ? "border-[#2563EB] bg-[#2563EB]/5 ring-2 ring-[#2563EB]/30"
                    : "border-border hover:border-[#2563EB]/50"
                }`}
              >
                <r.icon className={`w-5 h-5 mb-2 ${role === r.id ? "text-[#2563EB]" : "text-muted-foreground"}`} />
                <p className="font-heading font-semibold text-sm">{r.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="register-name-input" />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="register-phone-input" />
              </div>
            </div>
            {needsOrg && (
              <div>
                <Label htmlFor="company_name">
                  {role === "employer_owner" ? "Company name" : role === "institute_owner" ? "Institute name" : "Agency / firm name"}
                </Label>
                <Input
                  id="company_name"
                  required
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="mt-1.5"
                  data-testid="register-company-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="reg-email">Email</Label>
              <Input id="reg-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="reg-password">Password (min 8 characters)</Label>
              <Input id="reg-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="register-password-input" />
            </div>
            {needsOrg && (
              <p className="text-xs text-muted-foreground bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-lg px-3 py-2">
                {role === "employer_owner"
                  ? "Your first job post is free — pay only ₹499 + GST per additional job, or use prepaid wallet credits. No subscription required."
                  : "Your account will be reviewed and approved by the Job Spring admin team."}
              </p>
            )}
            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="register-error">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0B1B3D] hover:bg-[#13264f]" data-testid="register-submit-btn">
              {busy ? "Creating account…" : "Create Account"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-[#2563EB] font-semibold" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
